import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from "reactstrap";
import BaseTable from "../../Components/Common/BaseTable";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess } from "Components/constants/enums";
import {
  API_FETCH_ENQUIRIES,
  API_UPDATE_INQUIRY_STATUS,
} from "API/Services/common";
import "../../STYLE/EnquiryPage.css";
import BaseSelectDropdown from "../../Components/Common/BaseSelectDropdown";
import {
  ACTION_HEADER,
  BUTTON_LABEL,
  CANCEL_LABEL,
  DISABLED_LABEL,
  EMAIL_HEADER,
  ENQUIRY_LABEL,
  FIRST_NAME_HEADER,
  INQUIRIES_LABEL,
  INQUIRY_TITLE,
  LAST_NAME_HEADER,
  PENDING,
  PENDING_LABEL,
  PHONE_NUMBER_HEADER,
  POINTER_LABEL,
  RESOLVE,
  RESOLVED,
  RESOLVED_LABEL,
  SEARCH_INQUIRIES,
  STATUS_HEADER,
  STATUS_LABEL,
  UPDATE_LABEL,
} from "Components/constants/commonNames";
import BaseInput from "Components/Common/BaseInput";

interface Enquiry {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  status: string;
  message: string;
}

interface PaginationInfo {
  totalPages: number;
  totalRecordsCount: number;
  currentPage: number;
  numberOfRows: number;
}

const EnquiryPage: React.FC = () => {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loader, setLoader] = useState(false);
  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [modal, setModal] = useState(false);
  const [selectedInquiryId, setSelectedInquiryId] = useState<number | null>(
    null
  );
  const [selectedStatus, setSelectedStatus] = useState<string>(PENDING);
  const [error, setError] = useState(false);
  const [remarks, setRemarks] = useState<string>("");
  const [searchValue, setSearchValue] = useState("");
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
    totalPages: 1,
    totalRecordsCount: 0,
    currentPage: 1,
    numberOfRows: 10,
  });

  const fetchEnquiries = async () => {
    setError(false);
    setLoader(true);
    setFullScreenLoader(true);

    const payload = {
      searchBar: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
      pageSize: pageSize,
      page: page,
    };

    try {
      const response = await API_FETCH_ENQUIRIES(payload);
      if (checkStatusCodeSuccess(response?.statusCode)) {
        const mappedEnquiries = response.data.listOfInquiries.map(
          (enquiry: any) => ({
            id: enquiry.id,
            first_name: enquiry.first_name,
            last_name: enquiry.last_name,
            email: enquiry.email,
            phone_number: enquiry.phone_number,
            status: enquiry.status,
            message: enquiry.message,
          })
        );

        setEnquiries(mappedEnquiries);
        setPaginationInfo({
          totalPages: response?.data?.totalPages,
          totalRecordsCount: response?.data?.totalRecordsCount,
          currentPage: response?.data?.currentPage,
          numberOfRows: response.data.numberOfRows,
        });
      } else {
        setPaginationInfo((prev) => ({
          ...prev,
          numberOfRows: 0,
        }));
      }
    } catch (error: any) {
      setError(true);
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoader(false);
      setFullScreenLoader(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "first_name",
        header: FIRST_NAME_HEADER,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        accessorKey: "last_name",
        header: LAST_NAME_HEADER,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        accessorKey: "email",
        header: EMAIL_HEADER,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        accessorKey: "phone_number",
        header: PHONE_NUMBER_HEADER,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        accessorKey: "status",
        header: STATUS_HEADER,
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }: any) => {
          const statusValue = getValue().toLowerCase();
          return (
            <span
              className={`chip ${
                statusValue === RESOLVE.toLowerCase() ? RESOLVED : PENDING
              }`}
            >
              {getValue()}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: ACTION_HEADER,
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: any) => {
          const isResolved =
            row.original.status.toLowerCase() === RESOLVE.toLowerCase();
          return (
            <div className="d-flex">
              <i
                title="Edit"
                className={`mdi mdi-file-edit-outline fs-4 text-info ${
                  isResolved ? DISABLED_LABEL : POINTER_LABEL
                }`}
                onClick={
                  isResolved ? undefined : () => handleEdit(row.original.id)
                }
                role={BUTTON_LABEL}
                aria-disabled={isResolved}
              />
            </div>
          );
        },
      },
    ],
    []
  );

  const handleEdit = (id: number) => {
    setSelectedInquiryId(id);
    setModal(true);
  };

  const updateInquiryStatus = async () => {
    const payload = { status: selectedStatus, remarks: remarks };
    setModal(false);
    setFullScreenLoader(true);
    setLoader(true);
    try {
      const response = await API_UPDATE_INQUIRY_STATUS(
        selectedInquiryId!,
        payload
      );
      if (checkStatusCodeSuccess(response?.statusCode)) {
        toast.success(response.message);
        fetchEnquiries();
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setFullScreenLoader(false);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPage(1);
    setPageSize(newPageSize);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSort = (column: string, direction: "asc" | "desc") => {
    setColumnName(column);
    setSortOrder(direction);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    if (value !== searchValue) {
      setPage(1);
      setSearchValue(value);
    }
  };

  const statusOptions = [
    { value: PENDING, label: PENDING_LABEL },
    { value: RESOLVED, label: RESOLVED_LABEL },
  ];

  useEffect(() => {
    fetchEnquiries();
  }, [searchValue, columnName, sortOrder, pageSize, page]);

  document.title = INQUIRY_TITLE;

  return (
    <div className="top-div">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-1 mt-1 m-1">
          <span className="fs-4 mx-2">{INQUIRIES_LABEL}</span>
        </div>

        <div className="card">
          <div className="card-body">
            <BaseTable
              columns={columns}
              data={enquiries}
              isGlobalFilter={true}
              customPageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSearch={handleSearch}
              onSort={handleSort}
              tableClass="table bg-white"
              divClass="text-nowrap"
              manualPagination={true}
              numberOfRows={paginationInfo.numberOfRows}
              tableLoader={loader}
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              totalRecords={paginationInfo.totalRecordsCount}
              SearchPlaceholder={SEARCH_INQUIRIES}
            />
          </div>
        </div>

        <Modal
          isOpen={modal}
          toggle={() => setModal(!modal)}
          className="modal-dialog-centered modal-md"
        >
          <ModalHeader toggle={() => setModal(!modal)}>
            {STATUS_LABEL}
          </ModalHeader>
          <ModalBody>
            <BaseSelectDropdown
              isClearable={false}
              className="basic-select"
              classNamePrefix="select"
              isDisabled={false}
              isLoading={false}
              name="status"
              options={statusOptions}
              onChange={(option: any) => setSelectedStatus(option.value)}
              value={statusOptions.find(
                (option) => option.value === selectedStatus
              )}
            />
            <BaseInput
              label="Remarks"
              name="remarks"
              type="textarea"
              placeholder="Enter remarks"
              className="mt-2"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="success" onClick={updateInquiryStatus}>
              {UPDATE_LABEL}
            </Button>
            <Button color="danger" onClick={() => setModal(false)}>
              {CANCEL_LABEL}
            </Button>
          </ModalFooter>
        </Modal>

        {(fullScreenLoader || loader) && (
          <div className="full-screen-loader">
            <Spinner />
          </div>
        )}
</div>
    </div>
  );
};

export default EnquiryPage;
